// ═══════════════════════════════════════════════════════════════════════════
// ComplianceOverrideModal — review and acknowledge compliance / fatigue warnings
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { logComplianceOverride, logFatigueAcknowledgement } from '../utils/auditLog';

const OVERRIDE_REASONS = [
  { value: 'staffConsent', label: 'Staff member consented' },
  { value: 'emergencyCover', label: 'Emergency / last-minute cover' },
  { value: 'wtdOptOut', label: 'WTD opt-out signed' },
  { value: 'managementApproval', label: 'Management approval' },
  { value: 'shortTerm', label: 'Short-term exception only' },
  { value: 'other', label: 'Other (specify below)' },
];

export default function ComplianceOverrideModal({
  isOpen,
  onClose,
  onConfirm,
  companyId,
  userId,
  userName,
  errors = [],
  warnings = [],
  fatigueData = null,
  shiftId,
  rotaId,
  staffId,
}) {
  const { t } = useTranslation();
  const [selectedReason, setSelectedReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);

  const effectiveReason = selectedReason === 'other'
    ? customReason.trim()
    : OVERRIDE_REASONS.find(r => r.value === selectedReason)?.label || '';

  const canConfirm = effectiveReason.length > 0 && acknowledged;

  const handleConfirm = async () => {
    // Log the override
    try {
      if (errors.length > 0 || warnings.length > 0) {
        await logComplianceOverride(companyId, {
          userId, userName, shiftId, rotaId, staffId,
          reason: effectiveReason,
          overriddenErrors: errors,
          overriddenWarnings: warnings,
        });
      }
      if (fatigueData) {
        await logFatigueAcknowledgement(companyId, {
          userId, userName, staffId,
          fatigueScore: fatigueData.score,
          fatigueLevel: fatigueData.level,
          reason: effectiveReason,
          affectedShiftIds: fatigueData.affectedShiftIds || [shiftId],
        });
      }
    } catch (err) {
      console.error('Error logging override:', err);
    }

    onConfirm(effectiveReason);
    setSelectedReason('');
    setCustomReason('');
    setAcknowledged(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
      <div className="bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg border border-gray-700">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-red-800/50 bg-red-900/20 rounded-t-xl">
          <div className="flex items-center gap-2">
            <span className="text-2xl">⚠️</span>
            <h2 className="text-lg font-bold text-red-400">
              {t('override.title', 'Compliance Warning Review')}
            </h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-xl">&times;</button>
        </div>

        <div className="p-5 space-y-4">
          {/* Show violations */}
          {errors.length > 0 && (
            <div className="space-y-1">
              <div className="text-sm font-medium text-red-400">{t('override.errors', 'Compliance Errors:')}</div>
              {errors.map((e, i) => (
                <div key={i} className="flex items-start gap-2 text-sm text-red-300 bg-red-900/20 rounded p-2">
                  <span>❌</span><span>{e}</span>
                </div>
              ))}
            </div>
          )}

          {warnings.length > 0 && (
            <div className="space-y-1">
              <div className="text-sm font-medium text-amber-400">{t('override.warnings', 'Warnings:')}</div>
              {warnings.map((w, i) => (
                <div key={i} className="flex items-start gap-2 text-sm text-amber-300 bg-amber-900/20 rounded p-2">
                  <span>⚠️</span><span>{w}</span>
                </div>
              ))}
            </div>
          )}

          {fatigueData && (
            <div className="bg-orange-900/20 border border-orange-700/50 rounded-lg p-3">
              <div className="text-sm font-medium text-orange-400 mb-1">
                {t('override.fatigueWarning', 'Fatigue Risk Warning')}
              </div>
              <div className="text-sm text-orange-300">
                Score: <strong>{fatigueData.score}/100</strong> ({fatigueData.level})
              </div>
              {fatigueData.recommendations?.slice(0, 2).map((r, i) => (
                <div key={i} className="text-xs text-orange-400/70 mt-1">• {r}</div>
              ))}
            </div>
          )}

          {/* Reason selection */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              {t('override.reason', 'Reason for Proceeding')} *
            </label>
            <div className="space-y-2">
              {OVERRIDE_REASONS.map(reason => (
                <label key={reason.value} className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                  <input type="radio" name="reason" value={reason.value}
                    checked={selectedReason === reason.value}
                    onChange={e => setSelectedReason(e.target.value)}
                    className="text-blue-600" />
                  {reason.label}
                </label>
              ))}
            </div>
            {selectedReason === 'other' && (
              <textarea value={customReason} onChange={e => setCustomReason(e.target.value)}
                placeholder={t('override.specifyReason', 'Please specify the reason...')}
                rows={2}
                className="w-full mt-2 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm" />
            )}
          </div>

          {/* Acknowledgement */}
          <label className="flex items-start gap-2 text-sm text-gray-300 cursor-pointer bg-gray-900 p-3 rounded-lg">
            <input type="checkbox" checked={acknowledged}
              onChange={e => setAcknowledged(e.target.checked)}
              className="mt-0.5 text-blue-600" />
            <span>
              {t('override.acknowledge',
                'I acknowledge these warnings and understand my name and reason will be recorded in the audit log.')}
            </span>
          </label>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-5 border-t border-gray-700">
          <button onClick={onClose} className="px-4 py-2 text-gray-400 hover:text-white text-sm">
            {t('common.cancel', 'Cancel')}
          </button>
          <button onClick={handleConfirm} disabled={!canConfirm}
            className="px-5 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium">
            {t('override.confirm', 'Acknowledge & Proceed')}
          </button>
        </div>
      </div>
    </div>
  );
}
